// ============================================================================
// @torre/expenses — Estados de cuenta en CSV, Excel y OFX (#953, épica #959)
// ============================================================================
// Lectura SIN IA de lo que exporta el banco:
//
//   · CSV (coma, punto y coma, tabulador o barra; comillas; BOM)
//   · Excel: el front lee la hoja con exceljs y entrega las filas; aquí se
//     tratan igual que las de un CSV
//   · OFX / QFX (SGML 1.x o XML 2.x), de cuentas de cheques y de tarjeta
//
// Formatos mexicanos: fechas `dd/mm/aaaa`, `15-sep-2026`, `15 SEP 26`; montos
// `1,234.56`, `-$1,234.56`, `(1,234.56)`, `1.234,56` y `1,234.56 CR`.
//
// El mapeo de columnas se guarda como plantilla por cuenta: se aplica solo la
// siguiente vez si el archivo trae los mismos encabezados.
//
// Sin I/O. Igual que accounts.ts: nada importado de `./index` en el nivel
// superior (se evalúa antes que index).
// ============================================================================

import { BASE_CURRENCY, EXPENSE_CATEGORIES, isIsoDate, normalizeText, round2, type ExpenseCategory, type ExtractedCharge } from "./index";

export const STATEMENT_FORMATS = ["csv", "xlsx", "ofx"] as const;
export type StatementFormat = (typeof STATEMENT_FORMATS)[number];

/** Tipos MIME que el servidor acepta para un estado de cuenta sin IA. */
export const STATEMENT_MIME_TYPES = [
  "text/csv",
  "text/plain",
  "application/csv",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/x-ofx",
  "application/ofx",
  "application/vnd.intu.qfx",
  "application/octet-stream",
] as const;

/** Máximo de filas que se aceptan de un archivo. */
export const MAX_STATEMENT_ROWS = 2000;

export function statementFormatOf(fileName: string | null | undefined): StatementFormat | null {
  const n = String(fileName || "").toLowerCase();
  if (n.endsWith(".csv") || n.endsWith(".txt")) return "csv";
  if (n.endsWith(".xlsx")) return "xlsx";
  if (n.endsWith(".ofx") || n.endsWith(".qfx")) return "ofx";
  return null;
}

// --- Números y fechas -------------------------------------------------------------
export type DecimalSeparator = "." | ",";

/**
 * Lee un monto como lo escribe un banco mexicano. Devuelve `null` si no hay
 * número (celda vacía, "—", texto). Negativo si trae `-` (antes o después),
 * paréntesis o sufijo `CR`/`DR` según `crIsNegative`.
 */
export function parseMxNumber(value: unknown, decimal: DecimalSeparator = "."): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (value == null) return null;
  let s = String(value).trim();
  if (!s) return null;
  let negative = false;
  if (/^\(.*\)$/.test(s)) {
    negative = true;
    s = s.slice(1, -1).trim();
  }
  // "CR" marca un abono (se devuelve negativo); "DR" un cargo (sin cambio).
  const suffix = /\s*(CR|DR)$/i.exec(s);
  if (suffix) {
    if (suffix[1].toUpperCase() === "CR") negative = !negative;
    s = s.slice(0, suffix.index).trim();
  }
  if (/[-−–]$/.test(s)) {
    negative = !negative;
    s = s.slice(0, -1).trim();
  }
  s = s.replace(/\s+/g, "");
  // Signo y moneda en cualquier orden: "-$1,234.56", "$-1,234.56", "MXN -12".
  for (let m = /^([-−–+]|US\$|\$|MXN|USD|EUR|MN)/i.exec(s); m; m = /^([-−–+]|US\$|\$|MXN|USD|EUR|MN)/i.exec(s)) {
    if (/^[-−–]$/.test(m[1])) negative = !negative;
    s = s.slice(m[1].length);
  }
  s = s.replace(/(MXN|USD|EUR|MN)$/i, "");
  if (decimal === ",") s = s.replace(/\./g, "").replace(",", ".");
  else s = s.replace(/,/g, "");
  if (!/^\d+(\.\d+)?$/.test(s)) return null;
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  return negative ? -n : n;
}

export type DateOrder = "dmy" | "mdy" | "ymd";

const DAY_MS = 86_400_000;

const MONTH_NAMES: Record<string, number> = {
  ene: 1, enero: 1, jan: 1, january: 1,
  feb: 2, febrero: 2, february: 2,
  mar: 3, marzo: 3, march: 3,
  abr: 4, abril: 4, apr: 4, april: 4,
  may: 5, mayo: 5,
  jun: 6, junio: 6, june: 6,
  jul: 7, julio: 7, july: 7,
  ago: 8, agosto: 8, aug: 8, august: 8,
  sep: 9, sept: 9, set: 9, septiembre: 9, setiembre: 9, september: 9,
  oct: 10, octubre: 10, october: 10,
  nov: 11, noviembre: 11, november: 11,
  dic: 12, diciembre: 12, dec: 12, december: 12,
};

const pad2 = (n: number) => String(n).padStart(2, "0");
const iso = (y: number, m: number, d: number): string | null => {
  const s = `${y}-${pad2(m)}-${pad2(d)}`;
  return isIsoDate(s) ? s : null;
};
const fullYear = (y: number) => (y < 100 ? 2000 + y : y);

/**
 * Lee una fecha a `YYYY-MM-DD`. Acepta objetos Date (celdas de Excel), número
 * de serie de Excel, ISO, `dd/mm/aaaa` (o el orden indicado), separadores `/`,
 * `-`, `.` o espacio, y meses con nombre en español o inglés.
 */
export function parseMxDate(value: unknown, order: DateOrder = "dmy"): string | null {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return value.toISOString().slice(0, 10);
  }
  if (typeof value === "number") {
    // Número de serie de Excel (días desde 1899-12-30).
    if (value > 20000 && value < 80000) return new Date(Math.round((value - 25569) * DAY_MS)).toISOString().slice(0, 10);
    return null;
  }
  const s = String(value).trim().toLowerCase();
  let m = /^(\d{4})-(\d{2})-(\d{2})(?:[t\s].*)?$/.exec(s);
  if (m) return iso(Number(m[1]), Number(m[2]), Number(m[3]));
  m = /^(\d{8})(?:\d{6})?(?:\.\d+)?(?:\[.*\])?$/.exec(s);
  if (m) return iso(Number(s.slice(0, 4)), Number(s.slice(4, 6)), Number(s.slice(6, 8)));
  // Con nombre de mes: "15/sep/2026", "15 SEP 26", "sep 15, 2026".
  m = /^(\d{1,2})[\s/.-]+([a-záé]{3,10})\.?[\s/.,-]+(\d{2,4})$/.exec(s);
  if (m && MONTH_NAMES[m[2].normalize("NFD").replace(/[̀-ͯ]/g, "")]) {
    return iso(fullYear(Number(m[3])), MONTH_NAMES[m[2].normalize("NFD").replace(/[̀-ͯ]/g, "")], Number(m[1]));
  }
  m = /^([a-z]{3,10})\.?\s+(\d{1,2}),?\s+(\d{2,4})$/.exec(s);
  if (m && MONTH_NAMES[m[1]]) return iso(fullYear(Number(m[3])), MONTH_NAMES[m[1]], Number(m[2]));
  m = /^(\d{1,4})[/.\-\s](\d{1,2})[/.\-\s](\d{1,4})(?:\s+.*)?$/.exec(s);
  if (!m) return null;
  const [a, b, c] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (m[1].length === 4 || order === "ymd") return iso(a, b, c);
  if (order === "mdy") return iso(fullYear(c), a, b);
  return iso(fullYear(c), b, a);
}

/**
 * Deduce el orden de las fechas de una columna: si algún primer número pasa de
 * 12 es día-mes; si algún segundo pasa de 12, mes-día. Por defecto, el
 * mexicano (`dmy`).
 */
export function detectDateOrder(values: readonly unknown[]): DateOrder {
  let dmy = false;
  let mdy = false;
  for (const v of values) {
    if (typeof v !== "string") continue;
    const m = /^(\d{1,2})[/.-](\d{1,2})[/.-]\d{2,4}/.exec(v.trim());
    if (!m) continue;
    if (Number(m[1]) > 12) dmy = true;
    if (Number(m[2]) > 12) mdy = true;
  }
  if (mdy && !dmy) return "mdy";
  return "dmy";
}

// --- CSV --------------------------------------------------------------------------------
/** Separa un CSV en filas y celdas. Detecta el separador y respeta comillas. */
export function parseCsv(text: string, delimiter?: string): string[][] {
  const src = String(text || "").replace(/^﻿/, "");
  const delim = delimiter || detectDelimiter(src);
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i];
    if (quoted) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          cell += '"';
          i += 1;
        } else quoted = false;
      } else cell += ch;
      continue;
    }
    if (ch === '"' && cell.trim() === "") {
      quoted = true;
      cell = "";
    } else if (ch === delim) {
      row.push(cell.trim());
      cell = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && src[i + 1] === "\n") i += 1;
      row.push(cell.trim());
      rows.push(row);
      row = [];
      cell = "";
    } else cell += ch;
  }
  if (cell !== "" || row.length) {
    row.push(cell.trim());
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c !== ""));
}

function detectDelimiter(text: string): string {
  const lines = text.split(/\r?\n/).filter((l) => l.trim()).slice(0, 20);
  let best = ",";
  let bestScore = -1;
  for (const d of [",", ";", "\t", "|"]) {
    const counts = lines.map((l) => countOutsideQuotes(l, d));
    const usable = counts.filter((n) => n > 0);
    if (!usable.length) continue;
    // Separador bueno: aparece en muchas líneas y el mismo número de veces.
    const mode = usable.sort((a, b) => usable.filter((x) => x === b).length - usable.filter((x) => x === a).length)[0];
    const score = counts.filter((n) => n === mode).length * 10 + mode;
    if (score > bestScore) {
      bestScore = score;
      best = d;
    }
  }
  return best;
}

function countOutsideQuotes(line: string, d: string): number {
  let n = 0;
  let q = false;
  for (const ch of line) {
    if (ch === '"') q = !q;
    else if (ch === d && !q) n += 1;
  }
  return n;
}

// --- Mapeo de columnas --------------------------------------------------------------------
export const MAPPING_FIELDS = ["date", "description", "debit", "credit", "amount", "currency", "reference"] as const;
export type MappingField = (typeof MAPPING_FIELDS)[number];

/**
 * Cómo leer las filas de un archivo. Las columnas son índices (0…); `headers`
 * guarda los encabezados normalizados con que se armó, para reconocer el
 * mismo formato la próxima vez.
 */
export interface StatementMapping {
  /** Fila (0…) donde están los encabezados; -1 si el archivo no trae. */
  headerRow: number;
  columns: Partial<Record<MappingField, number>>;
  dateOrder: DateOrder;
  decimal: DecimalSeparator;
  /**
   * Con una sola columna de importe con signo: `cargo_negativo` (lo común en
   * cheques: -500 es un cargo) o `cargo_positivo` (tarjetas como AMEX: 500 es
   * un cargo y -500 un pago).
   */
  amountSign: "cargo_negativo" | "cargo_positivo";
  /** Moneda de las filas que no la traen. */
  currency?: string;
}

/** Plantilla guardada en la cuenta. */
export interface StatementTemplate extends StatementMapping {
  headers: string[];
  savedAt?: string;
}

const HEADER_HINTS: Record<MappingField, RegExp> = {
  date: /^(fecha( de)?( operacion| movimiento| transaccion| cargo| compra| aplicacion)?|fecha|date|transaction date|posted date|dia|f operacion|fecha oper)$/,
  description: /(descripcion|concepto|detalle|description|movimiento|establecimiento|comercio|merchant|referencia ampliada|narrativa)/,
  debit: /^(cargos?|retiros?|debitos?|debe|debit|withdrawals?|cargo mxn|importe cargo|salida)$/,
  credit: /^(abonos?|depositos?|creditos?|haber|credit|deposits?|abono mxn|importe abono|entrada)$/,
  amount: /^(importe|monto|amount|cantidad|importe mxn|monto mxn|valor|importe total)$/,
  currency: /^(moneda|divisa|currency)$/,
  reference: /^(referencia|folio|numero de referencia|reference|autorizacion|no referencia)$/,
};

const headerKey = (v: unknown) => normalizeText(v);

/** Índice de la fila de encabezados (la primera con fecha + importe o cargo). */
export function detectHeaderRow(rows: readonly (readonly unknown[])[]): number {
  for (let i = 0; i < Math.min(rows.length, 30); i += 1) {
    const keys = rows[i].map(headerKey);
    const has = (f: MappingField) => keys.some((k) => HEADER_HINTS[f].test(k));
    if (has("date") && (has("amount") || has("debit") || has("credit"))) return i;
  }
  return -1;
}

/** Propone el mapeo de un archivo que nunca se ha visto. */
export function guessMapping(rows: readonly (readonly unknown[])[]): StatementMapping {
  const headerRow = detectHeaderRow(rows);
  const columns: Partial<Record<MappingField, number>> = {};
  if (headerRow >= 0) {
    const keys = rows[headerRow].map(headerKey);
    for (const f of MAPPING_FIELDS) {
      const idx = keys.findIndex((k, i) => HEADER_HINTS[f].test(k) && !Object.values(columns).includes(i));
      if (idx >= 0) columns[f] = idx;
    }
  } else {
    // Sin encabezados: la primera columna con fechas y la última con números.
    const sample = rows.slice(0, 10);
    const width = Math.max(0, ...sample.map((r) => r.length));
    for (let c = 0; c < width; c += 1) {
      const vals = sample.map((r) => r[c]);
      if (columns.date === undefined && vals.filter((v) => parseMxDate(v)).length >= Math.ceil(sample.length / 2)) columns.date = c;
      else if (vals.filter((v) => parseMxNumber(v) != null).length >= Math.ceil(sample.length / 2)) columns.amount = c;
      else if (columns.description === undefined && vals.some((v) => typeof v === "string" && /[a-z]/i.test(v))) columns.description = c;
    }
  }
  const dataRows = rows.slice(headerRow + 1, headerRow + 40);
  const dateOrder = columns.date !== undefined ? detectDateOrder(dataRows.map((r) => r[columns.date as number])) : "dmy";
  const decimal = detectDecimal(dataRows.flatMap((r) => [columns.amount, columns.debit, columns.credit].filter((c): c is number => c !== undefined).map((c) => r[c])));
  return { headerRow, columns, dateOrder, decimal, amountSign: "cargo_negativo" };
}

function detectDecimal(values: readonly unknown[]): DecimalSeparator {
  let comma = 0;
  let dot = 0;
  for (const v of values) {
    if (typeof v !== "string") continue;
    if (/\d\.\d{3},\d{1,2}\b/.test(v) || /^-?\$?\d+,\d{2}$/.test(v.trim())) comma += 1;
    else if (/\d,\d{3}\.\d{1,2}\b/.test(v) || /\d\.\d{2}$/.test(v.trim())) dot += 1;
  }
  return comma > dot ? "," : ".";
}

/** Guarda el mapeo como plantilla con los encabezados con que se hizo. */
export function toTemplate(rows: readonly (readonly unknown[])[], mapping: StatementMapping, savedAt?: string): StatementTemplate {
  const headers = mapping.headerRow >= 0 ? (rows[mapping.headerRow] || []).map(headerKey) : [];
  return { ...mapping, headers, ...(savedAt ? { savedAt } : {}) };
}

/**
 * Aplica una plantilla guardada a un archivo nuevo. Busca la fila con los
 * mismos encabezados (aunque cambie de renglón o de orden) y reubica las
 * columnas por nombre. `null` si el archivo no tiene ese formato.
 */
export function applyTemplate(rows: readonly (readonly unknown[])[], template: StatementTemplate | null | undefined): StatementMapping | null {
  if (!template || !template.columns) return null;
  if (template.headerRow < 0 || !template.headers?.length) {
    return { headerRow: -1, columns: { ...template.columns }, dateOrder: template.dateOrder, decimal: template.decimal, amountSign: template.amountSign, currency: template.currency };
  }
  const wanted = Object.entries(template.columns)
    .filter(([, idx]) => idx !== undefined)
    .map(([f, idx]) => [f as MappingField, template.headers[idx as number]] as const)
    .filter(([, name]) => Boolean(name));
  for (let i = 0; i < Math.min(rows.length, 30); i += 1) {
    const keys = rows[i].map(headerKey);
    const columns: Partial<Record<MappingField, number>> = {};
    let ok = true;
    for (const [f, name] of wanted) {
      const idx = keys.indexOf(name);
      if (idx < 0) {
        ok = false;
        break;
      }
      columns[f] = idx;
    }
    if (ok && wanted.length) return { headerRow: i, columns, dateOrder: template.dateOrder, decimal: template.decimal, amountSign: template.amountSign, currency: template.currency };
  }
  return null;
}

/** Validación mínima de un mapeo antes de leer. */
export function validateMapping(m: StatementMapping): string[] {
  const errors: string[] = [];
  if (m.columns.date === undefined) errors.push("date");
  if (m.columns.description === undefined) errors.push("description");
  if (m.columns.amount === undefined && m.columns.debit === undefined && m.columns.credit === undefined) errors.push("amount");
  return errors;
}

/** Un movimiento leído del archivo, antes de ser cargo propuesto. */
export interface StatementRow {
  /** Renglón del archivo (1…), para señalar errores. */
  line: number;
  date: string;
  description: string;
  /** Siempre positivo; el sentido lo da `isCredit`. */
  amount: number;
  /** Abono (entra dinero a la cuenta / pago a la tarjeta). */
  isCredit: boolean;
  currency: string;
  reference?: string | null;
}

export interface MappedStatement {
  rows: StatementRow[];
  /** Renglones que parecían movimientos pero no se pudieron leer. */
  errors: { line: number; reason: "fecha" | "importe" }[];
  period: { from: string | null; to: string | null };
  /** Suma de cargos y de abonos por moneda. */
  totals: { debits: Record<string, number>; credits: Record<string, number> };
}

const cellText = (v: unknown) => (v == null ? "" : v instanceof Date ? v.toISOString().slice(0, 10) : String(v).trim());

/** Lee las filas con un mapeo. Se saltan renglones vacíos, saldos y totales. */
export function mapStatementRows(rows: readonly (readonly unknown[])[], mapping: StatementMapping, fallbackCurrency = BASE_CURRENCY): MappedStatement {
  const out: StatementRow[] = [];
  const errors: MappedStatement["errors"] = [];
  const col = mapping.columns;
  const at = (r: readonly unknown[], f: MappingField) => (col[f] === undefined ? undefined : r[col[f] as number]);
  for (let i = mapping.headerRow + 1; i < rows.length && out.length < MAX_STATEMENT_ROWS; i += 1) {
    const r = rows[i];
    if (!r || r.every((c) => cellText(c) === "")) continue;
    const rawDate = at(r, "date");
    const description = cellText(at(r, "description")).replace(/\s+/g, " ");
    const date = parseMxDate(rawDate, mapping.dateOrder);
    let signed: number | null = null;
    if (col.debit !== undefined || col.credit !== undefined) {
      const debit = parseMxNumber(at(r, "debit"), mapping.decimal);
      const credit = parseMxNumber(at(r, "credit"), mapping.decimal);
      if (debit && Math.abs(debit) > 0) signed = -Math.abs(debit);
      else if (credit && Math.abs(credit) > 0) signed = Math.abs(credit);
      else if (col.amount !== undefined) signed = parseMxNumber(at(r, "amount"), mapping.decimal);
    } else {
      const v = parseMxNumber(at(r, "amount"), mapping.decimal);
      if (v != null) signed = mapping.amountSign === "cargo_positivo" ? -v : v;
    }
    if (!date) {
      // Un renglón sin fecha y con importe es un subtotal o saldo: no es error.
      if (cellText(rawDate) && signed != null && !/saldo|total|balance/i.test(`${cellText(rawDate)} ${description}`)) errors.push({ line: i + 1, reason: "fecha" });
      continue;
    }
    if (signed == null || signed === 0) {
      if (!/saldo|balance/i.test(description)) errors.push({ line: i + 1, reason: "importe" });
      continue;
    }
    if (/^saldo (inicial|anterior|final)|^saldo al /i.test(normalizeText(description))) continue;
    const cur = cellText(at(r, "currency")).toUpperCase();
    out.push({
      line: i + 1,
      date,
      description: description || "Sin descripción",
      amount: round2(Math.abs(signed)),
      isCredit: signed > 0,
      currency: /^[A-Z]{3}$/.test(cur) ? cur : mapping.currency || fallbackCurrency,
      reference: cellText(at(r, "reference")) || null,
    });
  }
  return { rows: out, errors, period: periodOfRows(out), totals: rowTotals(out) };
}

function periodOfRows(rows: readonly StatementRow[]): { from: string | null; to: string | null } {
  if (!rows.length) return { from: null, to: null };
  const dates = rows.map((r) => r.date).sort();
  return { from: dates[0], to: dates[dates.length - 1] };
}

function rowTotals(rows: readonly StatementRow[]) {
  const debits: Record<string, number> = {};
  const credits: Record<string, number> = {};
  for (const r of rows) {
    const bucket = r.isCredit ? credits : debits;
    bucket[r.currency] = round2((bucket[r.currency] || 0) + r.amount);
  }
  return { debits, credits };
}

// --- OFX / QFX ---------------------------------------------------------------------------
export interface OfxStatement {
  bankId: string | null;
  /** Número de cuenta o tarjeta tal como viene (puede venir enmascarado). */
  accountNumber: string | null;
  last4: string | null;
  accountType: "cheques" | "credito" | null;
  currency: string;
  period: { from: string | null; to: string | null };
  /** Saldo final que declara el archivo (si viene). */
  ledgerBalance: number | null;
  rows: StatementRow[];
}

const ofxTag = (block: string, tag: string): string | null => {
  const m = new RegExp(`<${tag}>([^<\\r\\n]*)`, "i").exec(block);
  return m ? decodeOfx(m[1].trim()) || null : null;
};
const decodeOfx = (s: string) => s.replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">");

export function isOfx(text: string): boolean {
  return /<OFX>/i.test(text) || /OFXHEADER/i.test(text);
}

/** Lee un OFX 1.x (SGML, sin etiquetas de cierre) o 2.x (XML). */
export function parseOfx(text: string): OfxStatement {
  const src = String(text || "");
  const isCard = /<CCSTMTRS>/i.test(src) || /<CCACCTFROM>/i.test(src);
  const acctBlock = (/<(?:BANK|CC)ACCTFROM>([\s\S]*?)(?:<\/(?:BANK|CC)ACCTFROM>|<BANKTRANLIST>)/i.exec(src) || [])[1] || src;
  const accountNumber = ofxTag(acctBlock, "ACCTID");
  const digits = (accountNumber || "").replace(/\D/g, "");
  const currency = (ofxTag(src, "CURDEF") || BASE_CURRENCY).toUpperCase();
  const listBlock = (/<BANKTRANLIST>([\s\S]*?)(?:<\/BANKTRANLIST>|$)/i.exec(src) || [])[1] || src;
  const chunks = listBlock.split(/<STMTTRN>/i).slice(1);
  const rows: StatementRow[] = [];
  chunks.forEach((raw, i) => {
    const block = raw.split(/<\/STMTTRN>/i)[0];
    const date = parseMxDate(ofxTag(block, "DTPOSTED") || ofxTag(block, "DTUSER"), "ymd");
    const amount = parseMxNumber(ofxTag(block, "TRNAMT"));
    if (!date || amount == null || amount === 0) return;
    const name = ofxTag(block, "NAME") || "";
    const memo = ofxTag(block, "MEMO") || "";
    const description = [name, memo && memo !== name ? memo : ""].filter(Boolean).join(" — ") || "Sin descripción";
    const trnCurrency = (ofxTag(block, "CURSYM") || "").toUpperCase();
    rows.push({
      line: i + 1,
      date,
      description,
      amount: round2(Math.abs(amount)),
      // En OFX el signo es desde la cuenta: negativo = sale dinero / cargo.
      isCredit: amount > 0,
      currency: /^[A-Z]{3}$/.test(trnCurrency) ? trnCurrency : currency,
      reference: ofxTag(block, "FITID") || ofxTag(block, "REFNUM") || ofxTag(block, "CHECKNUM"),
    });
  });
  const balBlock = (/<LEDGERBAL>([\s\S]*?)(?:<\/LEDGERBAL>|<AVAILBAL>|$)/i.exec(src) || [])[1] || "";
  return {
    bankId: ofxTag(acctBlock, "BANKID") || ofxTag(src, "ORG"),
    accountNumber,
    last4: digits.length >= 4 ? digits.slice(-4) : null,
    accountType: isCard ? "credito" : /<BANKACCTFROM>/i.test(src) ? "cheques" : null,
    currency,
    period: {
      from: parseMxDate(ofxTag(listBlock, "DTSTART") || ofxTag(src, "DTSTART"), "ymd"),
      to: parseMxDate(ofxTag(listBlock, "DTEND") || ofxTag(src, "DTEND"), "ymd"),
    },
    ledgerBalance: balBlock ? parseMxNumber(ofxTag(balBlock, "BALAMT")) : null,
    rows,
  };
}

// --- De movimiento a cargo propuesto -------------------------------------------------------
const CATEGORY_HINTS: [ExpenseCategory, RegExp][] = [
  ["comisiones", /\b(comision|comisiones|anualidad|interes|intereses|iva comision|iva interes|cuota|membresia tarjeta|cargo por)\b/],
  ["impuestos", /\b(sat|impuesto|impuestos|isr|imss|infonavit|tenencia|predial|derechos)\b/],
  ["viajes", /\b(uber|didi|cabify|aerolineas?|vuelos?|aeromexico|volaris|viva ?aerobus|hotel|airbnb|booking|expedia|caseta|pemex|gasolin\w*)\b/],
  ["marketing", /\b(facebk|facebook|meta ads|google ads|linkedin ads|tiktok ads|mailchimp)\b/],
  ["oficina", /\b(office depot|officemax|papeleria|costco|walmart|sams)\b/],
  ["hardware", /\b(apple store|best buy|cyberpuerta|pcel|dell)\b/],
];

/** Categoría probable por el texto del banco; "otros" si no hay pista. */
export function guessCategory(text: string): ExpenseCategory {
  const t = normalizeText(text);
  for (const [cat, re] of CATEGORY_HINTS) if (re.test(t)) return cat;
  return "otros";
}

/** Limpia el texto del banco para usarlo como comercio. */
export function vendorFromDescription(description: string): string {
  const cleaned = String(description || "")
    .replace(/\s+—\s+.*$/, "")
    .replace(/\b(ref|referencia|aut|autorizacion|folio|rfc)[:.]?\s*[\w-]+/gi, "")
    .replace(/\b\d{6,}\b/g, "")
    .replace(/\*{2,}\d*/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();
  return cleaned || String(description || "").trim() || "Sin comercio";
}

/**
 * Convierte los movimientos leídos en cargos con la misma forma que devuelve
 * la IA, para que pasen por la misma revisión (duplicados, categoría,
 * plataforma) y la misma confirmación.
 */
export function rowsToCharges(rows: readonly StatementRow[], opts: { cardLast4?: string | null } = {}): ExtractedCharge[] {
  return rows.map((r) => {
    const category = guessCategory(r.description);
    return {
      date: r.date,
      vendor: vendorFromDescription(r.description),
      amount: r.amount,
      currency: r.currency,
      description: r.description,
      category: (EXPENSE_CATEGORIES as readonly string[]).includes(category) ? category : "otros",
      cardLast4: opts.cardLast4 && /^\d{4}$/.test(opts.cardLast4) ? opts.cardLast4 : null,
      isCredit: r.isCredit,
      confidence: "alta" as const,
      billedAmount: null,
      exchangeRate: r.currency === BASE_CURRENCY ? 1 : null,
    };
  });
}
